import { useEffect, useState } from "react";

// Common components
import MDBox from "components/MDBox";
import Status from "components/Table/Status";
import Author from "components/Table/Author";
import pxToRem from "assets/theme/functions/pxToRem";

// Material components
import { IconButton, Popover, Typography } from "@mui/material";

// 3rd party library
import moment from "moment";
import PropTypes from "prop-types";

// Utils
import { defaultData, Icons } from "utils/Constants";

export function ReportStatus({ statusList }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const checkPriorityStatus = (status) => {
    if (status.includes("checked")) return "checked";
    if (status.includes("in-discussion")) return "in-discussion";
    if (status.includes("closed")) return "closed";
    if (status.includes("submitted")) return "submitted";
    return "open";
  };

  const mainStatus = checkPriorityStatus(statusList);

  return (
    <MDBox display="flex" alignItems="center">
      <Status title={mainStatus.replace("-", " ")} />
      {!statusList.every((status) => status.includes(mainStatus)) && (
        <IconButton aria-label="report-info-status" color="info" onClick={handleClick}>
          {Icons.INFO}
        </IconButton>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Typography sx={{ p: 1, backgroundColor: "#f5f5f5", fontSize: pxToRem(16) }}>
          Open: {statusList.filter((status) => status.includes("open")).length}
        </Typography>
        <Typography sx={{ p: 1, backgroundColor: "#f5f5f5", fontSize: pxToRem(16) }}>
          Submitted: {statusList.filter((status) => status.includes("submitted")).length}
        </Typography>
        <Typography sx={{ p: 1, backgroundColor: "#f5f5f5", fontSize: pxToRem(16) }}>
          Closed: {statusList.filter((status) => status.includes("closed")).length}
        </Typography>
        <Typography sx={{ p: 1, backgroundColor: "#f5f5f5", fontSize: pxToRem(16) }}>
          Checked: {statusList.filter((status) => status.includes("checked")).length}
        </Typography>
        <Typography sx={{ p: 1, backgroundColor: "#f5f5f5", fontSize: pxToRem(16) }}>
          In Discussion: {statusList.filter((status) => status.includes("in-discussion")).length}
        </Typography>
      </Popover>
    </MDBox>
  );
}

export default function TowerData(reportProgressData) {
  const [rows, setRows] = useState([]);

  const handleReportType = (reportType) => {
    if (reportType.includes("Location based")) {
      return "Location";
    }
    if (reportType.includes("Asset per location")) {
      return "Asset";
    }
    if (reportType.includes("Multiple")) {
      return "Multiple";
    }
    return reportType;
  };

  const handlePercentage = (total, completed) => {
    const calculatePercentage = (totalCal, completedCal) =>
      totalCal > 0 ? (completedCal / totalCal) * 100 : 0;
    return calculatePercentage(total, completed);
  };

  useEffect(() => {
    if (reportProgressData) {
      // Organize data by scope
      const scopeMap = {};

      reportProgressData.forEach((report) => {
        report.reportData.forEach((item) => {
          const scopeName = item?.scope?.name || "Unknown Scope";
          const currentSortOrder = item?.scope?.sortOrder;

          if (!scopeMap[scopeName]) {
            // If this scope doesn’t exist yet — create it
            scopeMap[scopeName] = {
              items: [],
              scopeIndex: parseInt(scopeName.match(/\d+/)?.[0] || "0", 10),
              sortOrder: currentSortOrder,
            };
          } else if (
            // If it already exists — update sortOrder if newer or defined
            currentSortOrder !== undefined &&
            currentSortOrder !== null &&
            currentSortOrder !== scopeMap[scopeName].sortOrder
          ) {
            scopeMap[scopeName].sortOrder = currentSortOrder;
          }

          // Push item in any case
          scopeMap[scopeName].items.push({
            ...item,
            reportType: report.reportType,
          });
        });
      });

      // Sort scopes by scope number
      const sortedScopes = Object.keys(scopeMap).sort(
        (a, b) => scopeMap[a].sortOrder - scopeMap[b].sortOrder
      );

      // Generate rows
      const list = sortedScopes.flatMap((scopeName, scopeIndex) => {
        const scopeItems = scopeMap[scopeName].items;

        // Scope header row
        const header = {
          srNo: <Author name="" />,
          scope: <Author name={scopeName} maxContent={100} style={{ fontWeight: "bold" }} />,
          reportName: <Author name="" />,
          locasset: <Author name="" />,
          status: <Author name="" />,
          date: <Author name="" />,
          percentage: <Author name="" />,
        };

        // Individual report rows
        const reportData = scopeItems.map((item, itemIndex) => {
          const totalDuration = item?.totalDuration || 0;
          const completedDuration = item?.completedDuration || 0;
          const percentage = handlePercentage(totalDuration, completedDuration).toFixed(2);
          const reportTypeLabel = handleReportType(item.reportType);

          return {
            srNo: <Author name={`${scopeIndex + 1}.${itemIndex + 1}`} />,
            scope: <Author style={{ textTransform: "none" }} name={reportTypeLabel} />,
            reportName: (
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "max-content",
                  maxWidth: "250px",
                }}
              >
                <MDBox sx={{ width: "max-content", maxWidth: "230px" }}>
                  <Author
                    style={{ textTransform: "none" }}
                    name={item?.report?.title !== null ? item?.report?.title : ""}
                  />
                </MDBox>

                <IconButton
                  className="eye-icon-button"
                  aria-label="report-parameter"
                  color="info"
                  size="small"
                  disabled={!item?.userProjectReport}
                  sx={{ opacity: !item?.userProjectReport ? 0.5 : 1 }}
                  onClick={() =>
                    window.open(
                      `/client/report/report-details/${item?.userProjectReport}`,
                      "_blank"
                    )
                  }
                >
                  {Icons.VIEW}
                </IconButton>
              </MDBox>
            ),
            locasset: (
              <Author
                name={
                  item.reportType.includes("Location based report")
                    ? item?.location?.title
                    : `${item?.asset?.title || item?.asset?.cableName} (${
                        item?.asset?.fromLocation
                      }-${item?.asset?.toLocation})`
                }
                maxContent={defaultData.MEDIUM_CONTENT_LENGTH}
                style={{ textTransform: "none" }}
              />
            ),
            status:
              item?.status && item?.status !== null ? (
                <ReportStatus statusList={item?.status} />
              ) : (
                ""
              ),
            date:
              item?.completionDate && percentage.includes(100) ? (
                <Author
                  name={moment(item?.completionDate).format(defaultData.WEB_24_HOURS_FORMAT)}
                />
              ) : (
                ""
              ),
            percentage:
              percentage > 0 ? (
                <Author name={`${percentage < 100 && percentage > 0 ? percentage : 100}%`} />
              ) : (
                ""
              ),
          };
        });

        return [header, ...reportData];
      });

      setRows([...list]);
    }
  }, [reportProgressData]);

  return {
    columns: [
      { Header: "No.", accessor: "srNo", width: "5%" },
      { Header: "Scope", accessor: "scope", align: "left", width: "10%" },
      { Header: "Report Name", accessor: "reportName", align: "left", width: "50%" },
      { Header: "Location/Asset", accessor: "locasset", align: "left", width: "20%" },
      { Header: "Status", accessor: "status", width: "10%", align: "left" },
      { Header: "% Complete", accessor: "percentage", width: "1%", align: "center" },
    ],
    rows,
  };
}

ReportStatus.defaultProps = {
  statusList: [],
};

ReportStatus.propTypes = {
  statusList: PropTypes.arrayOf(PropTypes.string),
};
